/**
 * The modal every pop-up in the boards is built from.
 *
 * «SETTINGS PAGE - ?», «- КЭШ», the two change-password steps, both profile
 * pop-ups and «ADDS» are all the same construction: a blurred wash over the
 * screen you were on, a gradient panel on a large radius, sometimes a round
 * badge straddling its top-left corner, and sometimes a pill button pinned
 * near the bottom of the screen rather than inside the panel.
 *
 * Only five things vary — where the panel sits, whether there is a badge, what
 * is inside it, what is drawn beside it, and whether there is a bottom button —
 * so this is one component with a `size` rather than six near-identical ones. The sizes are
 * transcribed per board; Marina's panels drift by two or three percent between
 * frames, and each keeps its own numbers rather than being averaged.
 */
import type { ReactNode } from 'react';

/** Which board's panel geometry to use. See `.dlg-panel` in style.css. */
export type DialogSize =
  /** «?» and «КЭШ» — x10.0% y35.1% w84.6% r70. */
  | 'default'
  /** The three one-line forms — y39.8% h17.3% r70, button pinned at y88.4%. */
  | 'prompt'
  /** «Выберите аватар» — y24.1% h40.0% r90. */
  | 'picker'
  /** «ADDS» — y41.5% h10.2% r70, badge and chibi. */
  | 'notice'
  /** «Выберите язык» — y33.5% h26.7% r70, three option pills inside. */
  | 'language';

export function Dialog({
  badge,
  title,
  size = 'default',
  children,
  outside,
  action,
  onAction,
  actionDisabled,
  onClose,
  label,
}: {
  /** The glyph in the corner disc. Omitted on the forms, which have no badge. */
  badge?: string;
  title?: string;
  size?: DialogSize;
  children?: ReactNode;
  /**
   * Drawn as a sibling of the panel rather than inside it, so its percentages
   * resolve against the frame. The chibi and the cache dialog's progress bar
   * are both outside their panel on the boards, and nesting them would make
   * every offset relative to a panel whose height varies with its copy.
   */
  outside?: ReactNode;
  /** Label for the pill button below the panel. Omitted where there is none. */
  action?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  onClose: () => void;
  label: string;
}) {
  return (
    <div className={`dlg is-${size}`} role="dialog" aria-modal="true" aria-label={label}>
      {/* Tapping the wash dismisses, which is the only way out of the cache
          dialog — it has no button of its own in the design. */}
      <button className="dlg-scrim" onClick={onClose} aria-label="Закрыть" />
      <div className="dlg-panel">
        {badge && <span className="dlg-badge" aria-hidden="true">{badge}</span>}
        {title && <h2 className="dlg-title">{title}</h2>}
        {children}
      </div>
      {outside}
      {action && (
        <button className="dlg-action" onClick={onAction} disabled={actionDisabled}>
          {action}
        </button>
      )}
    </div>
  );
}
